import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, UploadCloud, Music } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COVERS = [
  "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=800&q=70",
  "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=800&q=70",
  "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=800&q=70",
  "https://images.unsplash.com/photo-1483393458019-411bc6bd104e?auto=format&fit=crop&w=800&q=70",
  "https://images.unsplash.com/photo-1487180144351-b8472da7d491?auto=format&fit=crop&w=800&q=70",
];

export default function UploadModal({ open, onClose, artists, onCreated }) {
  const [title, setTitle] = useState("");
  const [artistId, setArtistId] = useState("");
  const [genre, setGenre] = useState("");
  const [license, setLicense] = useState("49");
  const [audioUrl, setAudioUrl] = useState("https://www.soundhelix.com/examples/mp3/SoundHelix-Song-7.mp3");
  const [cover, setCover] = useState(COVERS[0]);
  const [loading, setLoading] = useState(false);

  if (!open) return null;
  const artist = artists.find((a) => a.id === artistId) || artists[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !artist) {
      toast.error("Falta título o artista");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/music/tracks`, {
        title: title.trim(),
        artist_id: artist.id,
        artist_name: artist.name,
        cover,
        audio_url: audioUrl,
        duration_sec: 200,
        genre: genre.trim() || "Independiente",
        license_price: parseFloat(license) || 0,
      });
      toast.success(`"${data.title}" publicado en Ovamx`);
      onCreated?.(data);
      onClose();
      setTitle(""); setGenre("");
    } catch {
      toast.error("No se pudo publicar el track");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="upload-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-ink/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-cream border hairline rounded-2xl shadow-2xl overflow-hidden"
      >
        <header className="flex items-center justify-between px-6 py-4 border-b hairline">
          <div className="flex items-center gap-2">
            <UploadCloud className="w-4 h-4 text-coral" />
            <h3 className="font-serif text-2xl">Publicar nueva obra</h3>
          </div>
          <button onClick={onClose} data-testid="upload-close" className="w-8 h-8 rounded-full hover:bg-cream-deep/60 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </header>

        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Título de la obra">
            <input data-testid="upload-title" required value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-white border hairline rounded-xl px-4 py-2.5 text-sm outline-none focus:border-coral" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Artista">
              <select data-testid="upload-artist" value={artistId} onChange={(e) => setArtistId(e.target.value)} className="w-full bg-white border hairline rounded-xl px-3 py-2.5 text-sm outline-none focus:border-coral">
                <option value="">— elige —</option>
                {artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </Field>
            <Field label="Género">
              <input data-testid="upload-genre" value={genre} onChange={(e) => setGenre(e.target.value)} placeholder="ej. R&B, Ambient" className="w-full bg-white border hairline rounded-xl px-4 py-2.5 text-sm outline-none focus:border-coral" />
            </Field>
          </div>

          <Field label="Licencia comercial (USD)">
            <input data-testid="upload-license" type="number" min="0" value={license} onChange={(e) => setLicense(e.target.value)} className="w-full bg-white border hairline rounded-xl px-4 py-2.5 text-sm outline-none focus:border-coral" />
          </Field>

          <Field label="URL del archivo de audio (mp3)">
            <input data-testid="upload-audio-url" type="url" value={audioUrl} onChange={(e) => setAudioUrl(e.target.value)} className="w-full bg-white border hairline rounded-xl px-4 py-2.5 text-sm outline-none focus:border-coral font-mono text-xs" />
          </Field>

          <Field label="Portada">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {COVERS.map((c) => (
                <button
                  type="button"
                  key={c}
                  data-testid={`upload-cover-${COVERS.indexOf(c)}`}
                  onClick={() => setCover(c)}
                  className={`w-16 h-16 rounded-md overflow-hidden shrink-0 ring-2 transition ${cover === c ? "ring-coral" : "ring-transparent hover:ring-ink/20"}`}
                >
                  <img src={c} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </Field>

          <div className="flex items-center justify-between gap-3 pt-3 border-t hairline">
            <p className="text-[11px] text-ink-mid font-mono uppercase tracking-[0.2em] inline-flex items-center gap-2">
              <Music className="w-3 h-3 text-coral" />
              0% comisión durante el primer año
            </p>
            <button data-testid="upload-submit" disabled={loading} type="submit" className="bg-ink text-cream px-6 py-2.5 rounded-full text-sm hover:bg-coral transition disabled:opacity-50">
              {loading ? "Publicando…" : "Publicar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
